import {} from '@/pages/camel-order/sites/components/OrderMethodDisplayNameSettingFormItem/type';
import { deliveryAreaForm } from '@/pages/delivery/in-house/service';
import { DeliveryArea } from '@/pages/delivery/in-house/types';
import {
  FormError,
  validBlank,
  validMaxNum,
  validMaxStr,
  validNumericFormat,
} from '@/validate';
import { validNum } from '@/validate/number';

const { MAX_40 } = validNum;

// 配達エリアのバリデーションを個別に定義
export type DeliveryAreaFormError = {
  name: FormError;
  postalCode: FormError;
  deliveryFee: FormError;
  asapDuration: FormError;
}[];

// 郵便番号のバリデーションを個別に定義
// 7桁の数字 or 末尾にアスタリスク(*)がある2桁以上の数字
const validatePostalCodeFormat = {
  check: (value: string): boolean => {
    const regExp = /^\d{7}$|^\d{2,}\*$/;
    return !regExp.test(value);
  },
  message: (label: string): string => `${label}の形式に誤りがあります`,
};

// 配達エリア内の郵便番号と重複しているかチェック
export const validateDuplicatedPostalCode = {
  check: (postCode: string, deliveryAreas: DeliveryArea[]): boolean => {
    // *が含まれている場合、*より前の部分を取得
    const basePostCode = postCode.includes('*')
      ? postCode.split('*')[0]
      : postCode;

    // 既存の郵便番号と重複しているかチェック
    return deliveryAreas.some((area) =>
      area.postal_codes.some((code) => {
        const baseCode = code.includes('*') ? code.split('*')[0] : code;
        // baseCodeがbasePostCodeと一致するか、basePostCodeで始まる場合、またはbaseCodeがbasePostCodeの一部である場合
        return (
          baseCode === basePostCode ||
          baseCode.startsWith(basePostCode) ||
          basePostCode.startsWith(baseCode)
        );
      })
    );
  },
  message: (label: string): string => `${label}は既に存在します`,
};

export const validateAreaName = (
  name: string,
  error: DeliveryAreaFormError,
  index: number
): DeliveryAreaFormError => {
  error[index].name.isTouched = true;
  const label = deliveryAreaForm.name.label;
  const isMax = validMaxStr.check(name, MAX_40);
  const isBlank = validBlank.check(name);
  if (isBlank) error[index].name.message = validBlank.message(label);
  if (isMax) error[index].name.message = validMaxStr.message(label, MAX_40);
  error[index].name.isError = isBlank || isMax;
  return error;
};

export const validatePostCode = (
  postCode: string,
  deliveryAreas: DeliveryArea[],
  error: DeliveryAreaFormError,
  index: number
): DeliveryAreaFormError => {
  error[index].postalCode.isTouched = true;
  const label = deliveryAreaForm.postalCode.label;
  const isPostalCode = validatePostalCodeFormat.check(postCode);
  const isDuplicated = validateDuplicatedPostalCode.check(
    postCode,
    deliveryAreas
  );
  const isBlank = validBlank.check(postCode);
  if (isBlank) error[index].postalCode.message = validBlank.message(label);
  if (isPostalCode)
    error[index].postalCode.message = validatePostalCodeFormat.message(label);
  if (isDuplicated)
    error[index].postalCode.message =
      validateDuplicatedPostalCode.message(label);
  error[index].postalCode.isError = isBlank || isPostalCode || isDuplicated;
  return error;
};

export const validateDeliveryFee = (
  deliveryFee: string,
  error: DeliveryAreaFormError,
  index: number
): DeliveryAreaFormError => {
  error[index].deliveryFee.isTouched = true;
  const label = deliveryAreaForm.deliveryFee.label;
  const isMax = validMaxNum.check(Number(deliveryFee), 99999);
  const isNumeric = validNumericFormat.check(deliveryFee.toString());
  if (isNumeric) {
    error[index].deliveryFee.message = validNumericFormat.message(label);
  }
  if (isMax)
    error[index].deliveryFee.message =
      '配達料は99,999円以下で入力してください。';

  error[index].deliveryFee.isError = isMax || isNumeric;
  return error;
};
